import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
// Step Progress Bar
export interface Step {
  id: string;
  label: string;
}
export interface StepProgressBarProps {
  steps: Step[];
  currentStep: number;
  className?: string;
}
export function StepProgressBar({
  steps,
  currentStep,
  className = ''
}: StepProgressBarProps) {
  return <nav aria-label="Progress" className={className} data-id="element-477">
      <ol className="flex items-center" data-id="element-478">
        {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        const isLast = index === steps.length - 1;
        return <li key={step.id} className={`relative ${isLast ? '' : 'flex-1'}`} data-id="element-479">
              <div className="flex items-center" data-id="element-480">
                {/* Step Circle */}
                <motion.div initial={false} animate={{
              backgroundColor: isCompleted ? '#0066FF' : isCurrent ? '#0066FF' : '#E8ECF1',
              borderColor: isCompleted || isCurrent ? '#0066FF' : '#E8ECF1'
            }} className={`
                    relative flex items-center justify-center
                    w-8 h-8 rounded-full border-2
                    transition-colors duration-300
                  `} data-id="element-481">
                  {isCompleted ? <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-482">
                      <CheckIcon className="w-4 h-4 text-white" data-id="element-483" />
                    </motion.div> : <span className={`text-body-sm font-medium ${isCurrent ? 'text-white' : 'text-surface-500'}`} data-id="element-484">
                      {index + 1}
                    </span>}
                </motion.div>

                {/* Connector Line */}
                {!isLast && <div className="flex-1 mx-2 h-0.5 bg-surface-200 relative overflow-hidden" data-id="element-485">
                    <motion.div initial={{
                width: '0%'
              }} animate={{
                width: isCompleted ? '100%' : '0%'
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="absolute inset-y-0 left-0 bg-primary-500" data-id="element-486" />
                  </div>}
              </div>

              {/* Step Label (visible on larger screens) */}
              <div className="hidden sm:block absolute -bottom-6 left-0 w-max" data-id="element-487">
                <span className={`text-caption ${isCompleted || isCurrent ? 'text-primary-500 font-medium' : 'text-surface-500'}`} data-id="element-488">
                  {step.label}
                </span>
              </div>
            </li>;
      })}
      </ol>
    </nav>;
}
// Percentage Progress Bar
export interface PercentageProgressBarProps {
  percentage: number;
  label?: string;
  showPercentage?: boolean;
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'success' | 'warning' | 'error';
  className?: string;
}
const sizeStyles = {
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3'
};
const colorStyles = {
  primary: 'bg-primary-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  error: 'bg-error-500'
};
export function PercentageProgressBar({
  percentage,
  label,
  showPercentage = true,
  size = 'md',
  color = 'primary',
  className = ''
}: PercentageProgressBarProps) {
  const clampedPercentage = Math.min(100, Math.max(0, percentage));
  return <div className={className} data-id="element-489">
      {(label || showPercentage) && <div className="flex items-center justify-between mb-1.5" data-id="element-490">
          {label && <span className="text-body-sm text-surface-700" data-id="element-491">{label}</span>}
          {showPercentage && <span className="text-body-sm font-medium text-surface-900 tabular-nums" data-id="element-492">
              {Math.round(clampedPercentage)}%
            </span>}
        </div>}
      <div className={`w-full bg-surface-200 rounded-full overflow-hidden ${sizeStyles[size]}`} role="progressbar" aria-valuenow={clampedPercentage} aria-valuemin={0} aria-valuemax={100} aria-label={label || 'Progress'} data-id="element-493">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${clampedPercentage}%`
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className={`h-full rounded-full ${colorStyles[color]}`} data-id="element-494" />
      </div>
    </div>;
}
// Processing Progress with Status
export interface ProcessingProgressProps {
  percentage: number;
  status: string;
  estimatedTime?: string;
  className?: string;
}
export function ProcessingProgress({
  percentage,
  status,
  estimatedTime,
  className = ''
}: ProcessingProgressProps) {
  return <div className={className} data-id="element-495">
      <div className="flex items-center justify-between mb-3" data-id="element-496">
        <motion.p key={status} initial={{
        opacity: 0,
        y: -5
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-body-md text-surface-700" data-id="element-497">
          {status}
        </motion.p>
        {estimatedTime && <span className="text-body-sm text-surface-500" data-id="element-498">{estimatedTime}</span>}
      </div>
      <PercentageProgressBar percentage={percentage} showPercentage={false} size="lg" data-id="element-499" />
      <div className="flex justify-end mt-1.5" data-id="element-500">
        <span className="text-body-sm font-medium text-primary-500 tabular-nums" data-id="element-501">
          {Math.round(percentage)}%
        </span>
      </div>
    </div>;
}